import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Fade from "@mui/material/Fade";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteIcon from "@mui/icons-material/Delete";
import Langs, { Localize } from "../../../../app/lang/langs";

/* ── keyframes for sticky footer entrance animation ── */
const fadeSlideUp = {
    "@keyframes fadeSlideUp": {
        "0%": { opacity: 0, transform: "translateY(24px)" },
        "100%": { opacity: 1, transform: "translateY(0)" },
    },
};

/**
 * MobileProjectDetailsTab - Mobile-first version of ProjectDetailsTab.
 * Full-screen, touch-friendly form with image upload, dates, steppers, status toggle, and sticky save button.
 */
export default function MobileProjectDetailsTab({
    project,
    validFields,
    mode,
    isMobile,
    fileInputRef,
    onImageChange,
    onImageClick,
    onDeleteImage,
    onDragOver,
    onDrop,
    onProjectNameChange,
    onDateChangeStart,
    onDateChangeEnd,
    onDelayChange,
    onNeededCollabsChange,
    onActiveChange,
    onSave,
    checkStartDateValidity,
}) {
    const formatDateForInput = (dateValue) => {
        if (!dateValue) return "";
        const d = dateValue instanceof Date ? dateValue : new Date(dateValue);
        if (isNaN(d.getTime())) return "";
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const handleStartDateChange = (e) => {
        const val = e.target.value;
        if (!val) return;
        const dateObj = new Date(val + "T00:00:00");
        onDateChangeStart({ value: dateObj });
    };

    const handleEndDateChange = (e) => {
        const val = e.target.value;
        if (!val) return;
        const dateObj = new Date(val + "T00:00:00");
        onDateChangeEnd({ value: dateObj });
    };

    const handleDelayIncrement = () => {
        const current = project.days_limit || 1;
        onDelayChange({ value: current + 1 });
    };

    const handleDelayDecrement = () => {
        const current = project.days_limit || 1;
        if (current > 1) {
            onDelayChange({ value: current - 1 });
        }
    };

    const handleCollabsIncrement = () => {
        const current = project.needed_collabs || 0;
        onNeededCollabsChange({ value: current + 1 });
    };

    const handleCollabsDecrement = () => {
        const current = project.needed_collabs || 0;
        if (current > 0) {
            onNeededCollabsChange({ value: current - 1 });
        }
    };

    const minEndDate = project.start_date
        ? formatDateForInput(project.start_date)
        : undefined;

    const sectionCard = {
        bgcolor: "var(--surface-card, #fff)",
        borderRadius: "16px",
        p: 2.5,
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    };

    const stepperButton = {
        minWidth: 48,
        minHeight: 48,
        width: 48,
        height: 48,
        borderRadius: "14px",
        bgcolor: "#FFF3E0",
        color: "#FF9015",
        border: "1.5px solid #FFE0B2",
        "&:hover": { bgcolor: "#FFE0B2" },
        "&:active": { transform: "scale(0.93)" },
        transition: "all 0.15s ease-in-out",
    };

    return (
        <Fade in timeout={350}>
            <Box
                sx={{
                    mt: 0,
                    px: 2,
                    pb: "100px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    minHeight: "100%",
                    overflowY: "auto",
                    WebkitOverflowScrolling: "touch",
                }}
            >
                {/* ─── Image Upload ─── */}
                <Box sx={sectionCard}>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={onImageChange}
                        ref={fileInputRef}
                        style={{ display: "none" }}
                    />
                    {project.image_src ? (
                        <Box sx={{ position: "relative", textAlign: "center" }}>
                            <Box
                                component="img"
                                src={project.image_src}
                                alt="Project"
                                onClick={onImageClick}
                                sx={{
                                    width: "100%",
                                    maxHeight: 200,
                                    objectFit: "cover",
                                    borderRadius: "14px",
                                    cursor: "pointer",
                                    transition: "opacity 0.2s ease",
                                    "&:active": { opacity: 0.85 },
                                }}
                            />
                            <IconButton
                                onClick={onDeleteImage}
                                sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    bgcolor: "rgba(0,0,0,0.55)",
                                    color: "#fff",
                                    width: 40,
                                    height: 40,
                                    "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                                    "&:active": { transform: "scale(0.9)" },
                                    backdropFilter: "blur(4px)",
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ) : (
                        <Box
                            onClick={onImageClick}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                height: 160,
                                borderRadius: "14px",
                                border: "2px dashed #E0E0E0",
                                bgcolor: "#FAFAFA",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                "&:active": {
                                    bgcolor: "#F5F5F5",
                                    borderColor: "#FF9015",
                                    transform: "scale(0.98)",
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    width: 64,
                                    height: 64,
                                    borderRadius: "50%",
                                    bgcolor: "#FFF3E0",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mb: 1.5,
                                }}
                            >
                                <AddAPhotoIcon sx={{ fontSize: 28, color: "#FF9015" }} />
                            </Box>
                            <Typography
                                variant="body2"
                                sx={{ color: "#888", fontWeight: 500 }}
                            >
                                <Langs str="ADD_IMAGE" />
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* ─── Project Name ─── */}
                <Box sx={sectionCard}>
                    <TextField
                        error={!validFields.nom && validFields.tried}
                        slotProps={{ htmlInput: { maxLength: 250 } }}
                        fullWidth
                        id="project-name"
                        label={<Langs str="PROJECT_NAME" />}
                        value={project.nom || ""}
                        onChange={(e) => onProjectNameChange(e)}
                        variant="outlined"
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "14px",
                                fontSize: "16px",
                                "& fieldset": {
                                    borderColor: !validFields.nom && validFields.tried
                                        ? undefined
                                        : "#E0E0E0",
                                },
                                "&:hover fieldset": {
                                    borderColor: "#FF9015",
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: "#FF9015",
                                    borderWidth: "2px",
                                },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                                color: "#FF9015",
                            },
                            "& input": {
                                fontSize: "16px",
                                padding: "14px 16px",
                            },
                        }}
                    />
                </Box>

                {/* ─── Dates ─── */}
                <Box sx={sectionCard}>
                    <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "#555", mb: 2 }}
                    >
                        <Langs str="START_DATE" /> & <Langs str="END_DATE" />
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "#888",
                                    fontWeight: 500,
                                    mb: 0.5,
                                    display: "block",
                                }}
                            >
                                <Langs str="START_DATE" />
                            </Typography>
                            <Box
                                component="input"
                                type="date"
                                value={formatDateForInput(project.start_date)}
                                onChange={handleStartDateChange}
                                disabled={checkStartDateValidity()}
                                sx={{
                                    width: "100%",
                                    fontSize: "16px",
                                    padding: "14px 16px",
                                    borderRadius: "14px",
                                    border: !validFields.start_date && validFields.tried
                                        ? "2px solid #d32f2f"
                                        : "1.5px solid #E0E0E0",
                                    bgcolor: checkStartDateValidity() ? "#F5F5F5" : "#fff",
                                    color: "#333",
                                    outline: "none",
                                    fontFamily: "inherit",
                                    WebkitAppearance: "none",
                                    "&:focus": {
                                        borderColor: "#FF9015",
                                        borderWidth: "2px",
                                        boxShadow: "0 0 0 3px rgba(255,144,21,0.12)",
                                    },
                                    "&:disabled": {
                                        opacity: 0.5,
                                    },
                                }}
                            />
                        </Box>

                        <Box>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "#888",
                                    fontWeight: 500,
                                    mb: 0.5,
                                    display: "block",
                                }}
                            >
                                <Langs str="END_DATE" />
                            </Typography>
                            <Box
                                component="input"
                                type="date"
                                value={formatDateForInput(project.end_date)}
                                onChange={handleEndDateChange}
                                disabled={!project.start_date}
                                min={minEndDate}
                                sx={{
                                    width: "100%",
                                    fontSize: "16px",
                                    padding: "14px 16px",
                                    borderRadius: "14px",
                                    border: "1.5px solid #E0E0E0",
                                    bgcolor: !project.start_date ? "#F5F5F5" : "#fff",
                                    color: "#333",
                                    outline: "none",
                                    fontFamily: "inherit",
                                    WebkitAppearance: "none",
                                    "&:focus": {
                                        borderColor: "#FF9015",
                                        borderWidth: "2px",
                                        boxShadow: "0 0 0 3px rgba(255,144,21,0.12)",
                                    },
                                    "&:disabled": {
                                        opacity: 0.5,
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                </Box>

                {/* ─── Delay (weeks) Stepper ─── */}
                <Box sx={sectionCard}>
                    <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "#555", mb: 2 }}
                    >
                        <Langs str="DELAY_WEEKS" />
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 3,
                        }}
                    >
                        <IconButton
                            onClick={handleDelayDecrement}
                            disabled={(project.days_limit || 1) <= 1}
                            sx={{
                                ...stepperButton,
                                opacity: (project.days_limit || 1) <= 1 ? 0.4 : 1,
                            }}
                        >
                            <RemoveIcon />
                        </IconButton>
                        <Box
                            sx={{
                                minWidth: 72,
                                height: 56,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "14px",
                                bgcolor: "#F5F5F5",
                                border: "1.5px solid #E0E0E0",
                            }}
                        >
                            <Typography
                                variant="h5"
                                sx={{ fontWeight: 700, color: "#333" }}
                            >
                                {project.days_limit || 1}
                            </Typography>
                        </Box>
                        <IconButton onClick={handleDelayIncrement} sx={stepperButton}>
                            <AddIcon />
                        </IconButton>
                    </Box>
                </Box>

                {/* ─── Needed Collabs Stepper (only for create mode) ─── */}
                {mode !== "edit" && !project.id && (
                    <Fade in timeout={300}>
                        <Box sx={sectionCard}>
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: "#555", mb: 2 }}
                            >
                                <Langs str="NEEDED_COLLABS_NUMBER" />
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 3,
                                }}
                            >
                                <IconButton
                                    onClick={handleCollabsDecrement}
                                    disabled={(project.needed_collabs || 0) <= 0}
                                    sx={{
                                        ...stepperButton,
                                        opacity: (project.needed_collabs || 0) <= 0 ? 0.4 : 1,
                                    }}
                                >
                                    <RemoveIcon />
                                </IconButton>
                                <Box
                                    sx={{
                                        minWidth: 72,
                                        height: 56,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderRadius: "14px",
                                        bgcolor: "#F5F5F5",
                                        border: "1.5px solid #E0E0E0",
                                    }}
                                >
                                    <Typography
                                        variant="h5"
                                        sx={{ fontWeight: 700, color: "#333" }}
                                    >
                                        {project.needed_collabs || 0}
                                    </Typography>
                                </Box>
                                <IconButton onClick={handleCollabsIncrement} sx={stepperButton}>
                                    <AddIcon />
                                </IconButton>
                            </Box>
                        </Box>
                    </Fade>
                )}

                {/* ─── Status Toggle ─── */}
                <Box sx={sectionCard}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box>
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: "#555" }}
                            >
                                <Langs str="PROJECT_STATUS" />
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: project.active ? "#4CAF50" : "#999",
                                    fontWeight: 600,
                                    mt: 0.25,
                                    display: "block",
                                }}
                            >
                                {project.active ? Localize("Active") : Localize("Inactive")}
                            </Typography>
                        </Box>
                        <Switch
                            checked={project.active === true}
                            onChange={(e) => onActiveChange(e.target.checked)}
                            color="warning"
                            sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": {
                                    color: "#FF9015",
                                },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                    backgroundColor: "#FF9015",
                                },
                            }}
                        />
                    </Box>
                </Box>

                {/* ─── Sticky Save / Next Button ─── */}
                <Box
                    sx={{
                        ...fadeSlideUp,
                        position: "fixed",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        px: 2.5,
                        py: 2,
                        pb: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
                        /* ── glassmorphic frosted surface ── */
                        background:
                            "linear-gradient(to top, rgba(255,255,255,0.92) 50%, rgba(255,255,255,0.78) 100%)",
                        backdropFilter: "blur(18px) saturate(1.6)",
                        WebkitBackdropFilter: "blur(18px) saturate(1.6)",
                        /* ── subtle orange-tinted top border ── */
                        borderTop: "1px solid rgba(255, 144, 21, 0.15)",
                        /* ── refined layered shadow for depth ── */
                        boxShadow:
                            "0 -4px 24px rgba(255, 144, 21, 0.06), 0 -1px 6px rgba(0, 0, 0, 0.04)",
                        zIndex: 10,
                        /* ── entrance animation ── */
                        animation: "fadeSlideUp 0.4s cubic-bezier(0, 0, 0.2, 1) both",
                    }}
                >
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={onSave}
                        sx={{
                            minHeight: 50,
                            borderRadius: "14px",
                            /* ── orange gradient ── */
                            background:
                                "linear-gradient(135deg, #FF9015 0%, #FFA940 100%)",
                            backgroundColor: "#FF9015",
                            color: "#fff",
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "0.95rem",
                            letterSpacing: "-0.01em",
                            border: "none",
                            /* ── layered glow shadow ── */
                            boxShadow:
                                "0 4px 16px rgba(255, 144, 21, 0.40), 0 8px 32px rgba(255, 144, 21, 0.20), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                            transition:
                                "all 280ms cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                                background:
                                    "linear-gradient(135deg, #FF9015 0%, #FFB74D 100%)",
                                backgroundColor: "#FF9015",
                                boxShadow:
                                    "0 6px 24px rgba(255, 144, 21, 0.50), 0 12px 40px rgba(255, 144, 21, 0.25)",
                                transform: "translateY(-2px)",
                            },
                            "&:active": {
                                transform: "scale(0.98)",
                                boxShadow:
                                    "0 2px 10px rgba(255, 144, 21, 0.35)",
                            },
                        }}
                    >
                        {mode === "edit" ? <Langs str="SAVE" /> : <Langs str="Next" />}
                    </Button>
                </Box>
            </Box>
        </Fade>
    );
}
